if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('[data-sticky-atc-select]');
        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.defaultLabel = this.querySelector('[data-sticky-atc-label]');
        this.addedLabel = this.querySelector('[data-sticky-atc-added]');
        this.textElement = this.querySelector('[data-sticky-atc-text]');
        this.priceElement = this.querySelector('[data-sticky-atc-price]');

        const variantsJson = this.querySelector('[data-sticky-atc-variants]');
        this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
        this.currentVariant =
          this.variants.find((variant) => variant.id === Number(this.dataset.currentVariant)) || {
            id: Number(this.dataset.currentVariant),
            available: this.dataset.currentAvailable === 'true',
            price: Number(this.dataset.currentPrice),
            options: [],
          };
      }

      connectedCallback() {
        const anchor = document.getElementById(this.dataset.anchorId);
        if (anchor && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            this.toggleVisibility(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
          });
          this.observer.observe(anchor);
        }

        if (this.select) this.select.addEventListener('change', this.onSelectChange.bind(this));
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));

        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.onQuantityChange = () => this.updatePrice();
        if (this.quantityInput) this.quantityInput.addEventListener('change', this.onQuantityChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers = [
            subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data.sectionId !== this.sectionId) return;
              this.onVariantChanged(event.data.variant);
            }),
            subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              if (event.source !== 'product-form') return;
              const addedId = Number(event.productVariantId);
              const isOwnVariant = this.variants.length
                ? this.variants.some((variant) => variant.id === addedId)
                : addedId === Number(this.dataset.currentVariant);
              if (isOwnVariant) this.flashAdded();
            }),
            subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updatePrice()),
          ];
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribers) this.unsubscribers.forEach((unsubscribe) => unsubscribe());
        if (this.quantityInput) this.quantityInput.removeEventListener('change', this.onQuantityChange);
        clearTimeout(this.addedTimeout);
      }

      toggleVisibility(show) {
        this.classList.toggle('sticky-atc--visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      get quantity() {
        const value = Number(this.quantityInput && this.quantityInput.value);
        return value > 0 ? value : 1;
      }

      onVariantChanged(variant) {
        if (variant && variant.id) {
          this.currentVariant = this.variants.find((item) => item.id === variant.id) || variant;
          if (this.select) this.select.value = String(variant.id);
        } else {
          this.currentVariant = null;
        }
        this.updateSubmit();
      }

      onSelectChange() {
        const variant = this.variants.find((item) => item.id === Number(this.select.value));
        if (!variant) return;
        this.currentVariant = variant;
        this.updateSubmit();
        this.syncMainPicker(variant);
      }

      // Mirrors the chosen variant back onto the theme's variant picker and fires a
      // single change event so the standard Dawn update flow (price, media, URL,
      // form input) runs exactly as if the shopper used the pills.
      syncMainPicker(variant) {
        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!picker) return;

        let changedInput = null;
        picker.querySelectorAll('.product-form__input').forEach((group, index) => {
          const value = variant.options[index];
          if (value === undefined) return;

          const dropdown = group.querySelector('select');
          if (dropdown) {
            const option = Array.from(dropdown.options).find((item) => item.value === value);
            if (!option) return;
            if (dropdown.value !== value || !option.hasAttribute('selected')) {
              const selected = dropdown.querySelector('option[selected]');
              if (selected) selected.removeAttribute('selected');
              option.setAttribute('selected', 'selected');
              if (dropdown.value !== value) {
                dropdown.value = value;
                changedInput = changedInput || dropdown;
              }
            }
          } else {
            const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
              (input) => input.value === value
            );
            if (radio && !radio.checked) {
              radio.checked = true;
              changedInput = changedInput || radio;
            }
          }
        });

        if (changedInput) changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSubmit() {
        if (this.submitButton.hasAttribute('disabled')) return;
        const mainButton = document.getElementById(this.dataset.submitId);
        if (!mainButton || mainButton.hasAttribute('disabled')) return;
        mainButton.click();
      }

      updateSubmit() {
        const variant = this.currentVariant;
        if (!variant) {
          this.submitButton.setAttribute('disabled', '');
          this.textElement.textContent = this.submitButton.dataset.unavailableText;
          this.priceElement.setAttribute('hidden', '');
        } else if (!variant.available) {
          this.submitButton.setAttribute('disabled', '');
          this.textElement.textContent = this.submitButton.dataset.soldOutText;
          this.priceElement.setAttribute('hidden', '');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.textElement.textContent = this.submitButton.dataset.addText;
          this.priceElement.removeAttribute('hidden');
          this.updatePrice();
        }
      }

      updatePrice() {
        if (!this.currentVariant || typeof this.currentVariant.price !== 'number') return;
        this.priceElement.textContent = this.formatMoney(this.currentVariant.price * this.quantity);
      }

      flashAdded() {
        this.defaultLabel.classList.add('hidden');
        this.addedLabel.classList.remove('hidden');
        this.submitButton.classList.add('sticky-atc__submit--added');
        clearTimeout(this.addedTimeout);
        this.addedTimeout = setTimeout(() => {
          this.addedLabel.classList.add('hidden');
          this.defaultLabel.classList.remove('hidden');
          this.submitButton.classList.remove('sticky-atc__submit--added');
        }, 1800);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = cents / 100;
        const withComma = (amount) => amount.replace('.', ',');

        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
          switch (placeholder) {
            case 'amount_no_decimals':
              return Math.round(value).toString();
            case 'amount_with_comma_separator':
              return withComma(value.toFixed(2));
            case 'amount_no_decimals_with_comma_separator':
              return Math.round(value).toString();
            default:
              return value.toFixed(2);
          }
        });
      }
    }
  );
}
